import { useUserStore } from "../../../../modules/user/stores";
import {
  getRedEnvelopeRainStatus,
  type RedEnvelopeRainStatus,
} from "../../../../api/redEnvelopeRain";
import { computeClockOffset } from "./schedule";

/**
 * 红包雨 status 的获取与短期复用（契约 rer-spec §10.5：同一活动 30s 内不重复调；领取后强制重拉）
 *
 * - 缓存键 = userId + activityId（账号切换后不会复用上一个账号的结果）；
 * - 偏移 offsetMs 在**收到响应的那一刻**按 serverTime 算好随缓存保存，复用缓存时不会因缓存变旧而算错倒计时；
 * - 同键在途请求合并（快速连点入口只发一次）；
 * - 请求发出后账号变化（登出 / 切号）→ 丢弃结果返回 null。
 */

/** 复用窗口 */
const STATUS_TTL_MS = 30 * 1000;

export interface RainStatusSnapshot {
  status: RedEnvelopeRainStatus;
  /** serverTime − 本机时间（收到响应时） */
  offsetMs: number;
  /** 本机收到时间 */
  receivedAt: number;
}

interface CacheEntry {
  key: string;
  snapshot: RainStatusSnapshot;
}

let cache: CacheEntry | null = null;
const inFlight = new Map<string, Promise<RainStatusSnapshot | null>>();
/** 请求序号：只有更晚发出的请求结果才写缓存 */
let requestSeq = 0;
let lastWrittenSeq = 0;

/** 当前登录用户 id（同步读全局用户状态；游客为 null） */
export function getRainUserId(): string | null {
  return useUserStore.getState().userInfo?.userId || null;
}

const buildKey = (userId: string, activityId: string) =>
  `${userId}|${activityId}`;

/** 让某活动（不传 = 全部）的缓存失效：领取后、日切、场次切换时调用 */
export function invalidateRainStatus(activityId?: string): void {
  if (!cache) return;
  if (!activityId || cache.key.endsWith(`|${activityId}`)) cache = null;
}

/**
 * 取 status。force = 跳过 30s 复用（领取后 / 场次切换 / 日切）。
 * 网络异常、无 model、请求期间账号变化 → null（调用方按「未知失败」兜底）。
 */
export async function fetchRainStatus(
  activityId: string,
  options: { force?: boolean } = {}
): Promise<RainStatusSnapshot | null> {
  const userId = getRainUserId();
  if (!userId || !activityId) return null;
  const key = buildKey(userId, activityId);

  if (
    !options.force &&
    cache &&
    cache.key === key &&
    Date.now() - cache.snapshot.receivedAt < STATUS_TTL_MS
  ) {
    return cache.snapshot;
  }

  const pending = inFlight.get(key);
  if (pending && !options.force) return pending;

  const seq = ++requestSeq;
  const doFetch = async (): Promise<RainStatusSnapshot | null> => {
    try {
      const status = await getRedEnvelopeRainStatus(activityId);
      // 请求期间切换了账号 / 登出：结果属于上一个用户，丢弃
      if (getRainUserId() !== userId) return null;
      if (!status || typeof status !== "object") return null;
      const snapshot: RainStatusSnapshot = {
        status,
        offsetMs: computeClockOffset(status.serverTime),
        receivedAt: Date.now(),
      };
      // 只让更晚发出的请求写缓存（强制重拉与旧的在途请求并发时，旧结果不覆盖新结果）
      if (seq > lastWrittenSeq) {
        lastWrittenSeq = seq;
        // show=false 不缓存：下次点入口仍然重新问后端（可能已恢复）
        if (status.show === true) {
          cache = { key, snapshot };
        } else if (cache?.key === key) {
          cache = null;
        }
      }
      return snapshot;
    } catch (error) {
      console.error("[RedEnvelopeRain] status failed:", error);
      return null;
    }
  };
  const request = doFetch();
  inFlight.set(key, request);
  // doFetch 内部已兜住异常，不会 reject
  request.then(() => {
    if (inFlight.get(key) === request) inFlight.delete(key);
  });
  return request;
}
